import { Injectable, OnDestroy } from '@angular/core';
import { delay, Subscription } from 'rxjs';
import { Models } from '@ibexfusion/models';
import { ConnectionService } from '../connection/connection.service';
import { StorageService } from '../storage/storage.service';
import { environment } from '../../../environments/environment';
import { ContentService } from '../content/content.service';
import { MessengerService } from '../messenger/messenger.service';
import { NavigationItemService } from '../navigation-item/navigation-item.service';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { MetaService } from '../meta/meta.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements OnDestroy {

  private static SEND_DELAY = 1000 * 60;
  private static LOG_CREATION_THRESHOLD = 1000;
  subscriptions: Subscription[] = [];
  private lastLogTimestamp = new Date().getTime();
  private lastLogPath = '';
  private activeDetail: string;
  private readonly source;
  private readonly mode;
  private analyticLogCache: Models.AnalyticLog[] = [];
  private lastSentTimestamp = 0;
  private userDevice: any;
  private packageId = (<any>window).forcePackage;

  private customer = { id: this.packageId, gaTracking: null, title: null as any };

  private eventListeners = [];

  constructor(
    private connectionService: ConnectionService, private storage: StorageService, private contentService: ContentService,
    private messenger: MessengerService, private navService: NavigationItemService, private router: Router, private meta: MetaService
  ) {
    this.source = 'Touch22';
    this.mode = 'ibextouch';

    let cachedAnalytics = this.storage.get('analyticLogCache') as any;
    this.analyticLogCache = [];
    try {
      cachedAnalytics = JSON.parse(cachedAnalytics);
      if (cachedAnalytics) {
        if (!_.isArray(cachedAnalytics)) {
          cachedAnalytics = [];
        }
        this.analyticLogCache = cachedAnalytics;
        this.submitAnalyticLogs();
      }
    } catch (err) {
      console.warn(err);
      this.submitAnalyticLogs();
    }
    this.addEventListener(window, 'beforeunload', () => {
      this.submitAnalyticLogs();
    });

    this.subscriptions.push(meta.$changed.subscribe(changed => {
      this.customer.gaTracking = this.meta.getString('gaTracking');
      this.customer.title = this.meta.getString('title');
    }));
  }

  addEventListener(element, action, callback, options?) {
    element.addEventListener(action, callback, options);
    this.eventListeners.push({ element, action, callback});
  }

  public logPageView(path: string) {
    if (!path) {
      return;
    }
    if (path.endsWith('(overlay:screensaver)')) {
      return;
    }
    if (!this.messenger.mainPackage) {
      setTimeout(() => {
        this.logPageView(path);
      }, 250);
      return;
    }
    if (!this.checkLog(new Date().getTime(), path)) {
      return;
    }

    if (path.indexOf('(app:') > -1) {
      let paths = path.split('(app:');
      path = paths[0].split('home/app')[1] + paths[1].substring(4, paths[1].length - 1);
      paths = path.split('/');
      if (paths[1] === paths[2]) {
        paths.splice(1, 1);
        path = paths.join('/');
      }
      if (this.activeDetail) {
        if (path.split('/').includes(this.activeDetail)) {
          return;
        }
        path += ('/' + this.activeDetail);
      }
    } else if (path?.startsWith('/home/app')) {
      return;
    }
    if (path === '/') {
      path = '/home';
    }
    if (path !== '/' && path !== '/not-found') {
      this.createLog('pageview', path);
    }
  }

  public logPageAction(action) {
    this.createLog('action', action);
  }

  public ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.length = 0;
    this.eventListeners.forEach(listener => {
      listener?.element?.removeEventListener(listener.action, listener.callback);
    });
    this.eventListeners = [];
  }

  public setActiveDetail(id, isString = false) {
    this.activeDetail = this.contentService.getEntry(id)?.title ? this.navService.prettifyPath(this.contentService.getEntry(id).title) : (isString ? id : null);
    if (this.activeDetail) {
      this.logPageView(this.router.url);
    }
  }

  private createLog(type: string, message: any) {
    const log = new Models.AnalyticLog();
    log.populateFromJSON({
      type, customers: [this.customer], message, DeviceID: this.userDevice, timestamp: Date.now(), source: this.source,
      mode: this.mode, language: this.messenger.currentLanguage, userAgent: navigator.userAgent
    });
    this.analyticLogCache.push(log);
    this.submitAnalyticLogs();
  }

  private submitAnalyticLogs() {
    if (!environment.production || !!window['disableAnalytics']) {
      console.log(1, 'log is disabled!: ', this.analyticLogCache);
      return;
    }
    for (let i = 0; i < this.analyticLogCache.length; i++) {
      if (this.analyticLogCache[i].message.indexOf('not-found') > -1
        || (this.analyticLogCache[i] && this.analyticLogCache[i - 1]
          && this.analyticLogCache[i].message === this.analyticLogCache[i - 1].message
          && this.analyticLogCache[i].timestamp - this.analyticLogCache[i - 1].timestamp < 1000)
      ) {
        this.analyticLogCache.splice(i, 0);
        i--;
      }
    }
    const subscription = this.storage.$ready.pipe(delay(0)).subscribe(ready => {
      if (ready) {
        subscription.unsubscribe();
        if (this.messenger.$initialized.getValue() && this.checkTimestamp(new Date().getTime()) && !!this.analyticLogCache && this.analyticLogCache.length > 0) {
          this.connectionService.create('/analytic-logs', this.analyticLogCache).then(() => {
            this.analyticLogCache = [];
            this.storage.set('analyticLogCache', JSON.stringify(this.analyticLogCache));
            this.lastSentTimestamp = new Date().getTime();
          }).catch(err => {
            console.log('could not send log: ', err);
            this.storage.set('analyticLogCache', JSON.stringify(this.analyticLogCache));
          });
        } else {
          this.storage.set('analyticLogCache', JSON.stringify(this.analyticLogCache));
        }
      }
    });
  }

  private checkTimestamp(timestamp: number): boolean {
    return timestamp - this.lastSentTimestamp > AnalyticsService.SEND_DELAY;
  }

  private checkLog(timestamp, path): boolean {
    if (path !== this.lastLogPath || Math.abs(timestamp - this.lastLogTimestamp) > AnalyticsService.LOG_CREATION_THRESHOLD) {
      this.lastLogPath = path;
      this.lastLogTimestamp = timestamp;
      return true;
    } else {
      return false;
    }
  }
}
